import * as React from 'react';
import { memo } from 'react';
import { Card, Divider, Row, Space, Typography } from 'antd';
import { ArticleWithRelations } from '../admin-knowledge-base.type';
import { LeftCircleOutlined } from '@ant-design/icons';
import Table, { ColumnType } from 'antd/es/table';
import { types } from '../admin-knowledge-base.constants';
import ArticlesTable, { ArticlesTableProps } from './articles-table';
import { ArticleRelations, Types } from '/react4xp/parts/knowledge-base-common/knowledge-base-common.type';

type SearchResultsViewProps = {
    query: string;
    goBack: () => void;
    items: ArticleWithRelations[];
} & Omit<ArticlesTableProps, 'articles'>;

const getKey = (relations: ArticleRelations['relations']) => {
    const { section, category, tag } = relations;
    return [section, category, tag].join('|');
};

const SearchResultsView = memo(
    ({
        goBack,
        query,
        items,
        setActive,
        showDeleteForm,
        showUpdateForm,
    }: SearchResultsViewProps) => {
        const groups = items.reduce((acc, item) => {
            const key = getKey(item.relations);
            if (acc.has(key)) {
                acc.get(key).push(item);
            } else {
                acc.set(key, [item]);
            }
            return acc;
        }, new Map<string, ArticleWithRelations[]>());

        const outerColumns: ColumnType<ArticleRelations['relations']>[] = [
            Types.SECTION,
            Types.CATEGORY,
            Types.TAG,
        ].map((item) => ({
            key: getKey(item as any),
            title: types[item],
            dataIndex: item,
        }));

        const outerData = Array.from(groups.keys())
            .map((key) => ({
                key: key,
                ...groups.get(key)[0].relations,
            }))
            .sort((a, b) => a.key.localeCompare(b.key));

        const expandedRowRender = (record: ArticleRelations['relations']) => {
            return (
                <ArticlesTable
                    articles={groups.get(getKey(record))}
                    setActive={setActive}
                    showDeleteForm={showDeleteForm}
                    showUpdateForm={showUpdateForm}
                />
            );
        };

        return (
            <Card>
                <Row
                    justify="space-between"
                    align="middle"
                >
                    <Space size={24}>
                        <LeftCircleOutlined
                            onClick={goBack}
                            style={{ cursor: 'pointer', fontSize: '2rem' }}
                        />
                        <Space direction="vertical">
                            <span className="text-muted">{`По вашему запросу "${query}" найдено:`}</span>
                            <Typography.Title
                                level={5}
                            >{`${items.length} результатов`}</Typography.Title>
                        </Space>
                    </Space>
                </Row>
                <Divider />
                <Table
                    columns={outerColumns}
                    dataSource={outerData}
                    expandable={{
                        expandedRowRender,
                        defaultExpandedRowKeys: Array.from(groups.keys()),
                    }}
                    scroll={{ x: 'max-content' }}
                    pagination={false}
                />
            </Card>
        );
    }
);

export default SearchResultsView;
